/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Chat Session Management
 * 
 * Handles session creation, loading, restoration, and persistence.
 */

import { QueryClient } from '@tanstack/react-query';
import {
  getChatSession,
  getChatSessionByKey,
  createChatSession,
  updateChatSession,
  generateChatSessionTitle
} from '@shared/lib/api/chat';
import { getUserSettings, updateSettingsCategory } from '@shared/lib/api/user_settings';
import { ChatState, ChatAction } from './types';

export class SessionManager {
  constructor(
    private dispatch: React.Dispatch<ChatAction>,
    private queryClient: QueryClient
  ) {}

  /**
   * Create a new session only when needed (lazy creation)
   */
  async ensureSession(state: ChatState): Promise<number> {
    if (state.dbSessionId) {
      return state.dbSessionId; // Session already exists
    }

    const sessionKey = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const dbSession = await createChatSession({
      sessionKey,
      isExpanded: state.isExpanded,
      sidebarWidth: state.sidebarWidth,
      title: 'New Chat',
      metadata: { createdFrom: 'lazy-creation' }
    });
    
    this.dispatch({ 
      type: 'SET_DB_SESSION', 
      payload: { 
        dbSessionId: dbSession.id, 
        sessionKey: dbSession.sessionKey,
        sessionTitle: dbSession.title || undefined 
      }
    });

    // Update user settings to track current session
    try {
      const userSettings = await this.queryClient.fetchQuery(getUserSettings);
      const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
      await updateSettingsCategory({ 
        category: 'chatSettings', 
        settings: { ...currentChatSettings, currentSessionId: dbSession.id }
      });
    } catch (error) {
      console.error('Error updating user settings with current session:', error);
    }
    
    return dbSession.id;
  }

  /**
   * Load an existing session by ID
   */
  async loadSession(sessionId: number, state: ChatState, isSwitchingSession: React.MutableRefObject<boolean>): Promise<void> {
    try {
      this.dispatch({ type: 'SET_LOADING', payload: true });
      isSwitchingSession.current = true;
      
      const session = await getChatSession(sessionId);
      
      this.dispatch({ 
        type: 'SET_DB_SESSION', 
        payload: { 
          dbSessionId: session.id, 
          sessionKey: session.sessionKey,
          sessionTitle: session.title || undefined 
        }
      });
      
      this.dispatch({ type: 'SET_EXPANDED', payload: session.isExpanded || false });
      this.dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: session.sidebarWidth || 320 });
      
      // Load plan if available
      this.dispatch({ type: 'SET_PLAN', payload: (session as any).plan || null });
      
      // Load Honcho state if available
      if (session.honchoWorkspaceId || session.honchoSessionId || session.honchoUserPeerId) {
        this.dispatch({ 
          type: 'SET_HONCHO_STATE', 
          payload: {
            workspaceId: session.honchoWorkspaceId ?? undefined,
            honchoSessionId: session.honchoSessionId ?? undefined,
            peerId: session.honchoUserPeerId ?? undefined
          }
        });
        this.dispatch({ type: 'SET_INITIALIZED', payload: session.isInitialized || false });
      }

      // Update user settings to track current session
      try {
        const userSettings = await this.queryClient.fetchQuery(getUserSettings);
        const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
        await updateSettingsCategory({ 
          category: 'chatSettings', 
          settings: { ...currentChatSettings, currentSessionId: sessionId }
        });
      } catch (error) {
        console.error('Error updating user settings with loaded session:', error);
      }
      
    } catch (error) {
      console.error('Error loading session:', error);
      throw error;
    } finally {
      this.dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  /**
   * Load an existing session by session key
   */
  async loadSessionByKey(sessionKey: string): Promise<void> {
    try {
      this.dispatch({ type: 'SET_LOADING', payload: true });
      
      const session = await getChatSessionByKey(sessionKey);
      
      this.dispatch({ 
        type: 'SET_DB_SESSION', 
        payload: { 
          dbSessionId: session.id, 
          sessionKey: session.sessionKey,
          sessionTitle: session.title || undefined 
        }
      });
      
      this.dispatch({ type: 'SET_EXPANDED', payload: session.isExpanded || false });
      this.dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: session.sidebarWidth || 320 });
      
      // Load plan if available
      this.dispatch({ type: 'SET_PLAN', payload: (session as any).plan || null });
      
      // Load Honcho state if available
      if (session.honchoWorkspaceId || session.honchoSessionId || session.honchoUserPeerId) {
        this.dispatch({ 
          type: 'SET_HONCHO_STATE', 
          payload: {
            workspaceId: session.honchoWorkspaceId ?? undefined,
            honchoSessionId: session.honchoSessionId ?? undefined,
            peerId: session.honchoUserPeerId ?? undefined
          }
        });
        this.dispatch({ type: 'SET_INITIALIZED', payload: session.isInitialized || false });
      }
      
    } catch (error) {
      console.error('Error loading session by key:', error);
      throw error;
    } finally {
      this.dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  /**
   * Refresh just the plan from the current session
   * Used after agent execution completes to get any plan updates made by tools
   */
  async refreshSessionPlan(sessionId: number): Promise<void> {
    try {
      const session = await getChatSession(sessionId);
      if (session) {
        this.dispatch({ type: 'SET_PLAN', payload: (session as any).plan || null });
      }
    } catch (error) {
      console.error('Error refreshing session plan:', error);
    }
  }

  /**
   * Reset chat state and clear current session
   */
  async resetChat(state: ChatState): Promise<void> {
    // Mark current session as inactive if it exists
    if (state.dbSessionId) {
      try {
        await updateChatSession(state.dbSessionId, { isActive: false });
      } catch (error) {
        console.error('Error marking session as inactive:', error);
      }
    }

    // Clear current session state
    this.dispatch({ type: 'RESET_CHAT' });
    this.dispatch({ type: 'SET_INITIALIZED', payload: false });

    // Clear current session from user settings
    try {
      const userSettings = await this.queryClient.fetchQuery(getUserSettings);
      const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
      await updateSettingsCategory({ 
        category: 'chatSettings', 
        settings: { ...currentChatSettings, currentSessionId: undefined }
      });
    } catch (error) {
      console.error('Error clearing session from user settings:', error);
    }

    // Invalidate React Query cache to trigger real-time UI updates
    this.queryClient.invalidateQueries({ queryKey: ['/api/chat/sessions'] });
  }

  /**
   * Start a new session (for "New Chat" functionality)
   */
  async startNewSession(state: ChatState): Promise<any> {
    try {
      // Mark current session as inactive if it exists
      if (state.dbSessionId) {
        await updateChatSession(state.dbSessionId, { isActive: false });
      }

      // Clear current session state (don't create new session yet)
      this.dispatch({ type: 'RESET_CHAT' });
      this.dispatch({ type: 'SET_INITIALIZED', payload: false });

      // Clear current session from user settings
      const userSettings = await this.queryClient.fetchQuery(getUserSettings);
      const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
      await updateSettingsCategory({ 
        category: 'chatSettings', 
        settings: { ...currentChatSettings, currentSessionId: undefined }
      });

      // Invalidate React Query cache to trigger real-time UI updates
      this.queryClient.invalidateQueries({ queryKey: ['/api/chat/sessions'] });
      
      return null; // Return null for now, actual session creation happens on first message
    } catch (error) {
      console.error('Error starting new session:', error);
      throw error;
    }
  }

  /**
   * Create a new session with specific context data (for review sessions)
   */
  async createSessionWithContext(
    title: string, 
    context: Record<string, any>, 
    metadata?: Record<string, any>
  ): Promise<any> {
    try {
      const sessionKey = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const dbSession = await createChatSession({
        sessionKey,
        title,
        isExpanded: true, // Expand for review sessions
        sidebarWidth: 320,
        context,
        metadata: { 
          createdFrom: 'review-session',
          ...metadata 
        }
      });
      
      this.dispatch({ 
        type: 'SET_DB_SESSION', 
        payload: { 
          dbSessionId: dbSession.id, 
          sessionKey: dbSession.sessionKey,
          sessionTitle: dbSession.title || undefined 
        }
      });
      
      this.dispatch({ type: 'SET_EXPANDED', payload: true });
      
      // Update user settings to track current session
      try {
        const userSettings = await this.queryClient.fetchQuery(getUserSettings);
        const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
        await updateSettingsCategory({ 
          category: 'chatSettings', 
          settings: { ...currentChatSettings, currentSessionId: dbSession.id }
        });
      } catch (error) {
        console.error('Error updating user settings with new context session:', error);
      }
      
      return dbSession;
    } catch (error) {
      console.error('Error creating session with context:', error);
      throw error;
    }
  }

  /**
   * Schedule automatic title generation for a session
   */
  scheduleSessionTitleGeneration(
    sessionId: number, 
    state: ChatState,
    titleGenerationTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ): void {
    // Clear any existing title generation timeout
    if (titleGenerationTimeoutRef.current) {
      clearTimeout(titleGenerationTimeoutRef.current);
      titleGenerationTimeoutRef.current = null;
    }

    // Schedule title generation after 10 seconds
    titleGenerationTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await generateChatSessionTitle(sessionId);
        
        if (result?.updated && result?.title) {
          // Update the current session title in context if this is the current session
          if (sessionId === state.dbSessionId) {
            this.dispatch({ type: 'SET_SESSION_TITLE', payload: result.title });
          }
          // Invalidate queries to refresh the session list with new title
          this.queryClient.invalidateQueries({ queryKey: ['/api/chat/sessions'] });
        }
      } catch (error) {
        console.error('Failed to generate session title:', error);
        // Don't show user-facing error for title generation failures
        // as this is a background enhancement feature
      }
    }, 10000); // 10 seconds delay
  }

  /**
   * Try to restore user's last session on mount.
   * Returns the restored session ID if successful, undefined otherwise.
   */
  async tryRestoreSession(): Promise<number | undefined> {
    try {
      this.dispatch({ type: 'SET_LOADING', payload: true });
      
      // Try to get user's current session from settings
      const userSettings = await this.queryClient.fetchQuery(getUserSettings);
      
      const currentSessionId = userSettings?.chatSettings?.currentSessionId;
      if (currentSessionId) {
        try {
          await this.loadSession(currentSessionId, { dbSessionId: undefined } as ChatState, { current: false });
          return currentSessionId;
        } catch (error) {
          // Clear the invalid session from user settings
          const currentChatSettings = userSettings?.chatSettings || { deleteConfirmation: true };
          await updateSettingsCategory({ 
            category: 'chatSettings', 
            settings: { ...currentChatSettings, currentSessionId: undefined }
          });
        }
      }
      
      // Set default UI state but no session
      this.dispatch({ type: 'SET_EXPANDED', payload: false });
      this.dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: 320 });
      
      return undefined;
    } catch (error) {
      console.error('Error during session restoration:', error);
      // Set default state if restoration fails
      this.dispatch({ type: 'SET_EXPANDED', payload: false });
      this.dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: 320 });
      return undefined;
    } finally {
      this.dispatch({ type: 'SET_LOADING', payload: false });
    }
  }
}
